import { FC, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { MenuItem, TextField } from '@mui/material'
import dayjs from 'dayjs'
import { useNotification } from 'lib/context/notification.context'
import { MainLayout } from 'views/layouts'
import { BackButton, Button, DangerButton, GhostButton, StrokedButton } from 'views/components/UI/Buttons'
import Modal from 'views/components/UI/Modal'
import { H3, H5 } from 'views/components/UI/Text'
import getInventoryByCompany from 'services/inventory/getByCompany'
import { superAdminRoutes } from 'router/routes'
import {
  createCertificationProcess,
  createCertificationSession,
  deleteCertificationProcess,
  deleteCertificationSession,
  duplicateCertificationProcess,
  getCertificationProcessById,
  getCertificationProcesses,
  ICertificationProcess,
  ICertificationProcessPayload,
  ICertificationSession,
  ICertificationSessionPayload,
  updateCertificationProcess,
  updateCertificationSession,
} from 'services/certifications/processes'
import {
  Actions,
  Card,
  EmptyState,
  Form,
  FormGrid,
  FullWidth,
  Grid,
  Header,
  MetaChip,
  MetaRow,
  Page,
  RowButton,
  SectionTitle,
  Subtitle,
  Table,
  TableWrap,
  Td,
  Th,
  Title,
  TitleBlock,
} from './styled'

interface IProcessFormState {
  name: string
  cohort_name: string
  description: string
  registration_open_date: string
  registration_close_date: string
  status: string
  max_capacity: string
  additional_instructions: string
}

interface ISessionFormState {
  name: string
  session_date: string
  start_time: string
  end_time: string
  timezone: string
  teams_link: string
  description: string
  session_order: string
}

interface IDeleteConfirmTarget {
  kind: 'process' | 'session'
  id: number
  title: string
}

const emptyProcessForm: IProcessFormState = {
  name: '',
  cohort_name: '',
  description: '',
  registration_open_date: '',
  registration_close_date: '',
  status: 'draft',
  max_capacity: '',
  additional_instructions: '',
}

const emptySessionForm: ISessionFormState = {
  name: '',
  session_date: '',
  start_time: '',
  end_time: '',
  timezone: 'America/Bogota',
  teams_link: '',
  description: '',
  session_order: '1',
}

const CertificationProcesses: FC = (): JSX.Element => {
  const navigate = useNavigate()
  const params = useParams<Record<string, string | undefined>>()
  const { getSuccess, getWarning, getError } = useNotification()

  const companyId = Number(params.companyId)
  const subscriptionCompanyId = Number(params.subscriptionCompanyId)

  const [loading, setLoading] = useState<boolean>(false)
  const [inventoryName, setInventoryName] = useState<string>('')
  const [clientCode, setClientCode] = useState<string>('')
  const [processes, setProcesses] = useState<ICertificationProcess[]>([])
  const [selectedProcessId, setSelectedProcessId] = useState<number | null>(null)
  const [selectedProcessDetail, setSelectedProcessDetail] = useState<ICertificationProcess | null>(null)
  const [editingProcessId, setEditingProcessId] = useState<number | null>(null)
  const [editingSessionId, setEditingSessionId] = useState<number | null>(null)
  const [deleteConfirmTarget, setDeleteConfirmTarget] = useState<IDeleteConfirmTarget | null>(null)
  const [processForm, setProcessForm] = useState<IProcessFormState>(emptyProcessForm)
  const [sessionForm, setSessionForm] = useState<ISessionFormState>(emptySessionForm)

  const selectedProcess = useMemo(() => {
    if (selectedProcessDetail) {
      return selectedProcessDetail
    }

    if (selectedProcessId) {
      return processes.find((process) => process.id === selectedProcessId) || null
    }

    return null
  }, [processes, selectedProcessDetail, selectedProcessId])

  const resetProcessForm = (): void => {
    setProcessForm(emptyProcessForm)
    setEditingProcessId(null)
  }

  const resetSessionForm = (): void => {
    setSessionForm(emptySessionForm)
    setEditingSessionId(null)
  }

  const loadInventoryInfo = async (): Promise<void> => {
    if (!companyId || !subscriptionCompanyId) {
      return
    }

    try {
      const response: any = await getInventoryByCompany(companyId, 1, 1000)
      const inventories: any[] = response?.data || []
      const inventory = inventories.find(
        (item) => Number(item.subscription_company_id) === Number(subscriptionCompanyId),
      )

      if (inventory) {
        setInventoryName(inventory.name || '')
        setClientCode(inventory.client_code || '')
      }
    } catch {
      getWarning('No se pudo cargar la informacion del inventario.')
    }
  }

  const loadProcessDetail = async (processId: number): Promise<void> => {
    try {
      const process = await getCertificationProcessById(processId)
      setSelectedProcessDetail(process)
    } catch {
      setSelectedProcessDetail(null)
    }
  }

  const loadProcesses = async (): Promise<void> => {
    if (!subscriptionCompanyId) {
      return
    }

    setLoading(true)
    try {
      const data = await getCertificationProcesses(subscriptionCompanyId)
      setProcesses(data)

      if (data.length > 0) {
        const nextSelectedId =
          selectedProcessId && data.some((item) => item.id === selectedProcessId)
            ? selectedProcessId
            : data[0].id

        setSelectedProcessId(nextSelectedId)
      } else {
        setSelectedProcessId(null)
        setSelectedProcessDetail(null)
      }
    } catch {
      getError('No se pudieron cargar los procesos de certificacion.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadInventoryInfo()
    void loadProcesses()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, subscriptionCompanyId])

  useEffect(() => {
    if (selectedProcessId) {
      void loadProcessDetail(selectedProcessId)
      resetSessionForm()
    } else {
      setSelectedProcessDetail(null)
      resetSessionForm()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProcessId])

  const handleSelectProcess = (process: ICertificationProcess): void => {
    setSelectedProcessId(process.id)
    setEditingProcessId(process.id)
    setProcessForm({
      name: process.name || '',
      cohort_name: process.cohort_name || '',
      description: process.description || '',
      registration_open_date: process.registration_open_date
        ? dayjs(process.registration_open_date).format('YYYY-MM-DD')
        : '',
      registration_close_date: process.registration_close_date
        ? dayjs(process.registration_close_date).format('YYYY-MM-DD')
        : '',
      status: process.status || 'draft',
      max_capacity:
        process.max_capacity !== null && process.max_capacity !== undefined
          ? String(process.max_capacity)
          : '',
      additional_instructions: process.additional_instructions || '',
    })
  }

  const handleNewProcess = (): void => {
    resetProcessForm()
    setSelectedProcessId(null)
    setSelectedProcessDetail(null)
  }

  const handleSaveProcess = async (): Promise<void> => {
    if (!subscriptionCompanyId) {
      getWarning('No se encontro el inventario asociado.')
      return
    }

    if (!processForm.name.trim() || !processForm.cohort_name.trim()) {
      getWarning('Debes completar la certificacion y el proceso.')
      return
    }

    const payload: ICertificationProcessPayload = {
      subscription_company_id: subscriptionCompanyId,
      name: processForm.name.trim(),
      cohort_name: processForm.cohort_name.trim(),
      description: processForm.description.trim() || undefined,
      registration_open_date: processForm.registration_open_date || undefined,
      registration_close_date: processForm.registration_close_date || undefined,
      status: processForm.status,
      max_capacity: processForm.max_capacity ? Number(processForm.max_capacity) : null,
      additional_instructions: processForm.additional_instructions.trim() || undefined,
    }

    setLoading(true)
    try {
      if (editingProcessId) {
        await updateCertificationProcess(editingProcessId, payload)
        getSuccess('Proceso actualizado correctamente.')
      } else {
        await createCertificationProcess(payload)
        getSuccess('Proceso creado correctamente.')
      }

      resetProcessForm()
      await loadProcesses()
    } catch (error: any) {
      getError(error?.response?.data?.message || 'No se pudo guardar el proceso.')
    } finally {
      setLoading(false)
    }
  }

  const handleDuplicateProcess = async (process: ICertificationProcess): Promise<void> => {
    setLoading(true)
    try {
      await duplicateCertificationProcess(process.id)
      getSuccess('Proceso duplicado correctamente.')
      await loadProcesses()
    } catch (error: any) {
      getError(error?.response?.data?.message || 'No se pudo duplicar el proceso.')
    } finally {
      setLoading(false)
    }
  }

  const handleRequestDeleteProcess = (process: ICertificationProcess): void => {
    setDeleteConfirmTarget({
      kind: 'process',
      id: process.id,
      title: process.name,
    })
  }

  const handleRequestDeleteSession = (session: ICertificationSession): void => {
    setDeleteConfirmTarget({
      kind: 'session',
      id: session.id,
      title: session.name,
    })
  }

  const handleCloseDeleteModal = (value: boolean): void => {
    if (!value) {
      setDeleteConfirmTarget(null)
    }
  }

  const handleConfirmDelete = async (): Promise<void> => {
    if (!deleteConfirmTarget) {
      return
    }

    setLoading(true)
    try {
      if (deleteConfirmTarget.kind === 'process') {
        await deleteCertificationProcess(deleteConfirmTarget.id)
        getSuccess('Proceso eliminado correctamente.')
        if (selectedProcessId === deleteConfirmTarget.id) {
          setSelectedProcessId(null)
          setSelectedProcessDetail(null)
          resetProcessForm()
          resetSessionForm()
        }
      } else {
        await deleteCertificationSession(deleteConfirmTarget.id)
        getSuccess('Sesion eliminada correctamente.')
        if (selectedProcessDetail?.id) {
          await loadProcessDetail(selectedProcessDetail.id)
        }
      }

      await loadProcesses()
    } catch (error: any) {
      getError(error?.response?.data?.message || 'No se pudo eliminar el elemento.')
    } finally {
      setDeleteConfirmTarget(null)
      setLoading(false)
    }
  }

  const handleEditSession = (session: ICertificationSession): void => {
    setEditingSessionId(session.id)
    setSessionForm({
      name: session.name || '',
      session_date: session.session_date ? dayjs(session.session_date).format('YYYY-MM-DD') : '',
      start_time: session.start_time || '',
      end_time: session.end_time || '',
      timezone: session.timezone || 'America/Bogota',
      teams_link: session.teams_link || '',
      description: session.description || '',
      session_order: String(session.session_order || 1),
    })
  }

  const handleSaveSession = async (): Promise<void> => {
    if (!selectedProcessDetail?.id) {
      getWarning('Selecciona primero un proceso.')
      return
    }

    if (
      !sessionForm.name.trim() ||
      !sessionForm.session_date ||
      !sessionForm.start_time ||
      !sessionForm.end_time
    ) {
      getWarning('Completa los datos de la sesion.')
      return
    }

    const payload: ICertificationSessionPayload = {
      name: sessionForm.name.trim(),
      session_date: sessionForm.session_date,
      start_time: sessionForm.start_time,
      end_time: sessionForm.end_time,
      timezone: sessionForm.timezone.trim() || 'America/Bogota',
      teams_link: sessionForm.teams_link.trim() || undefined,
      description: sessionForm.description.trim() || undefined,
      session_order: Number(sessionForm.session_order),
    }

    setLoading(true)
    try {
      if (editingSessionId) {
        await updateCertificationSession(editingSessionId, payload)
        getSuccess('Sesion actualizada correctamente.')
      } else {
        await createCertificationSession(selectedProcessDetail.id, payload)
        getSuccess('Sesion creada correctamente.')
      }

      resetSessionForm()
      await loadProcessDetail(selectedProcessDetail.id)
      await loadProcesses()
    } catch (error: any) {
      getError(error?.response?.data?.message || 'No se pudo guardar la sesion.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <MainLayout>
      <Page>
        <Header>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <BackButton onClick={() => navigate(-1)} />
            <TitleBlock>
              <Title>Gestion de procesos</Title>
              <Subtitle>
                {inventoryName
                  ? `${inventoryName}${clientCode ? ` | Codigo ${clientCode}` : ''}`
                  : 'Administracion de procesos y sesiones de certificacion'}
              </Subtitle>
            </TitleBlock>
          </div>
          <StrokedButton
            type="button"
            onClick={() => navigate(superAdminRoutes.Inventory.replace(':companyId', String(companyId)))}
          >
            Volver al inventario
          </StrokedButton>
        </Header>

        <Grid>
          <Card>
            <SectionTitle>Procesos</SectionTitle>
            <MetaRow>
              <MetaChip>Inventario: {inventoryName || 'Sin nombre'}</MetaChip>
              <MetaChip>Cupos: {selectedProcessDetail?.max_capacity ?? 'No definido'}</MetaChip>
              <MetaChip>Procesos: {processes.length}</MetaChip>
            </MetaRow>

            <Actions style={{ marginBottom: 16 }}>
              <Button type="button" onClick={handleNewProcess}>
                Nuevo proceso
              </Button>
              {selectedProcess && (
                <StrokedButton type="button" onClick={() => handleDuplicateProcess(selectedProcess)}>
                  Duplicar seleccionado
                </StrokedButton>
              )}
            </Actions>

            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <Th>Certificacion</Th>
                    <Th>Proceso</Th>
                    <Th>Estado</Th>
                    <Th>Sesiones</Th>
                    <Th>Acciones</Th>
                  </tr>
                </thead>
                <tbody>
                  {processes.length > 0 ? (
                    processes.map((process) => (
                      <tr key={process.id}>
                        <Td>
                          <RowButton
                            type="button"
                            $active={selectedProcessId === process.id}
                            onClick={() => handleSelectProcess(process)}
                          >
                            {process.name}
                          </RowButton>
                        </Td>
                        <Td>{process.cohort_name}</Td>
                        <Td>{process.status}</Td>
                        <Td>{process.sessions_count || 0}</Td>
                        <Td>
                          <Actions>
                            <Button type="button" onClick={() => handleSelectProcess(process)}>
                              Editar
                            </Button>
                            <StrokedButton type="button" onClick={() => handleDuplicateProcess(process)}>
                              Duplicar
                            </StrokedButton>
                            <StrokedButton type="button" onClick={() => handleRequestDeleteProcess(process)}>
                              Eliminar
                            </StrokedButton>
                          </Actions>
                        </Td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <Td colSpan={5}>
                        <EmptyState>No hay procesos creados para este inventario.</EmptyState>
                      </Td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </TableWrap>

            <div style={{ marginTop: 20 }}>
              <SectionTitle>{editingProcessId ? 'Editar proceso' : 'Nuevo proceso'}</SectionTitle>
              <Form>
                <FormGrid>
                  <TextField
                    label="Certificacion"
                    value={processForm.name}
                    onChange={(event) => setProcessForm((prev) => ({ ...prev, name: event.target.value }))}
                    fullWidth
                  />
                  <TextField
                    label="Proceso"
                    value={processForm.cohort_name}
                    onChange={(event) =>
                      setProcessForm((prev) => ({ ...prev, cohort_name: event.target.value }))
                    }
                    fullWidth
                  />
                  <TextField
                    label="Estado"
                    select
                    value={processForm.status}
                    onChange={(event) => setProcessForm((prev) => ({ ...prev, status: event.target.value }))}
                    fullWidth
                  >
                    <MenuItem value="draft">draft</MenuItem>
                    <MenuItem value="published">published</MenuItem>
                    <MenuItem value="closed">closed</MenuItem>
                  </TextField>
                  <TextField
                    label="Cupo maximo"
                    type="number"
                    value={processForm.max_capacity}
                    onChange={(event) =>
                      setProcessForm((prev) => ({ ...prev, max_capacity: event.target.value }))
                    }
                    fullWidth
                  />
                  <TextField
                    label="Apertura de registro"
                    type="date"
                    value={processForm.registration_open_date}
                    onChange={(event) =>
                      setProcessForm((prev) => ({
                        ...prev,
                        registration_open_date: event.target.value,
                      }))
                    }
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                  <TextField
                    label="Cierre de registro"
                    type="date"
                    value={processForm.registration_close_date}
                    onChange={(event) =>
                      setProcessForm((prev) => ({
                        ...prev,
                        registration_close_date: event.target.value,
                      }))
                    }
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                  <FullWidth>
                    <TextField
                      label="Descripcion"
                      value={processForm.description}
                      onChange={(event) =>
                        setProcessForm((prev) => ({ ...prev, description: event.target.value }))
                      }
                      fullWidth
                      multiline
                      minRows={3}
                    />
                  </FullWidth>
                  <FullWidth>
                    <TextField
                      label="Instrucciones adicionales"
                      value={processForm.additional_instructions}
                      onChange={(event) =>
                        setProcessForm((prev) => ({
                          ...prev,
                          additional_instructions: event.target.value,
                        }))
                      }
                      fullWidth
                      multiline
                      minRows={3}
                    />
                  </FullWidth>
                </FormGrid>
                <Actions>
                  <Button type="button" onClick={handleSaveProcess} disabled={loading}>
                    {editingProcessId ? 'Guardar cambios' : 'Crear proceso'}
                  </Button>
                  <StrokedButton type="button" onClick={resetProcessForm}>
                    Limpiar
                  </StrokedButton>
                </Actions>
              </Form>
            </div>
          </Card>

          <Card>
            <SectionTitle>Sesiones</SectionTitle>
            {selectedProcess ? (
              <>
                <MetaRow>
                  <MetaChip>Proceso: {selectedProcess.cohort_name}</MetaChip>
                  <MetaChip>Certificacion: {selectedProcess.name}</MetaChip>
                  <MetaChip>Sesiones: {(selectedProcessDetail?.sessions || []).length}</MetaChip>
                </MetaRow>

                <TableWrap>
                  <Table style={{ minWidth: 780 }}>
                    <thead>
                      <tr>
                        <Th>Nombre</Th>
                        <Th>Fecha</Th>
                        <Th>Horario</Th>
                        <Th>Orden</Th>
                        <Th>Acciones</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedProcessDetail?.sessions || []).length > 0 ? (
                        (selectedProcessDetail?.sessions || []).map((session) => (
                          <tr key={session.id}>
                            <Td>{session.name}</Td>
                            <Td>{dayjs(session.session_date).format('DD/MM/YYYY')}</Td>
                            <Td>{`${session.start_time} - ${session.end_time}`}</Td>
                            <Td>{session.session_order}</Td>
                            <Td>
                              <Actions>
                                <Button type="button" onClick={() => handleEditSession(session)}>
                                  Editar
                                </Button>
                                <StrokedButton type="button" onClick={() => handleRequestDeleteSession(session)}>
                                  Eliminar
                                </StrokedButton>
                              </Actions>
                            </Td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <Td colSpan={5}>
                            <EmptyState>No hay sesiones creadas para este proceso.</EmptyState>
                          </Td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </TableWrap>

                <div style={{ marginTop: 20 }}>
                  <SectionTitle>{editingSessionId ? 'Editar sesion' : 'Nueva sesion'}</SectionTitle>
                  <Form>
                    <FormGrid>
                      <TextField
                        label="Nombre"
                        value={sessionForm.name}
                        onChange={(event) => setSessionForm((prev) => ({ ...prev, name: event.target.value }))}
                        fullWidth
                      />
                      <TextField
                        label="Orden"
                        type="number"
                        value={sessionForm.session_order}
                        onChange={(event) =>
                          setSessionForm((prev) => ({
                            ...prev,
                            session_order: event.target.value,
                          }))
                        }
                        fullWidth
                      />
                      <TextField
                        label="Fecha"
                        type="date"
                        value={sessionForm.session_date}
                        onChange={(event) =>
                          setSessionForm((prev) => ({
                            ...prev,
                            session_date: event.target.value,
                          }))
                        }
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                      />
                      <TextField
                        label="Zona horaria"
                        value={sessionForm.timezone}
                        onChange={(event) =>
                          setSessionForm((prev) => ({ ...prev, timezone: event.target.value }))
                        }
                        fullWidth
                      />
                      <TextField
                        label="Hora inicio"
                        type="time"
                        value={sessionForm.start_time}
                        onChange={(event) =>
                          setSessionForm((prev) => ({ ...prev, start_time: event.target.value }))
                        }
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                      />
                      <TextField
                        label="Hora fin"
                        type="time"
                        value={sessionForm.end_time}
                        onChange={(event) =>
                          setSessionForm((prev) => ({ ...prev, end_time: event.target.value }))
                        }
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                      />
                      <FullWidth>
                        <TextField
                          label="Enlace Teams"
                          value={sessionForm.teams_link}
                          onChange={(event) =>
                            setSessionForm((prev) => ({ ...prev, teams_link: event.target.value }))
                          }
                          fullWidth
                        />
                      </FullWidth>
                      <FullWidth>
                        <TextField
                          label="Descripcion"
                          value={sessionForm.description}
                          onChange={(event) =>
                            setSessionForm((prev) => ({ ...prev, description: event.target.value }))
                          }
                          fullWidth
                          multiline
                          minRows={3}
                        />
                      </FullWidth>
                    </FormGrid>
                    <Actions>
                      <Button type="button" onClick={handleSaveSession} disabled={loading}>
                        {editingSessionId ? 'Guardar sesion' : 'Crear sesion'}
                      </Button>
                      <StrokedButton type="button" onClick={resetSessionForm}>
                        Limpiar
                      </StrokedButton>
                    </Actions>
                  </Form>
                </div>
              </>
            ) : (
              <EmptyState>Selecciona un proceso para administrar sus sesiones.</EmptyState>
            )}
          </Card>
        </Grid>

        <Modal
          show={deleteConfirmTarget !== null}
          setShow={handleCloseDeleteModal}
          hideCloseButton={false}
          styleContent={{
            width: '560px',
            maxWidth: '90vw',
            borderRadius: '16px',
            padding: '0',
            backgroundColor: '#FFFFFF',
          }}
        >
          <div style={{ padding: '28px 30px 24px' }}>
            <H3 style={{ marginBottom: '10px' }}>
              {deleteConfirmTarget?.kind === 'process'
                ? '¿Seguro que deseas eliminar este proceso?'
                : '¿Seguro que deseas eliminar esta sesion?'}
            </H3>
            <H5 style={{ fontWeight: 400, lineHeight: 1.5, marginBottom: '24px' }}>
              {deleteConfirmTarget?.kind === 'process'
                ? `Vas a eliminar "${deleteConfirmTarget.title}". Si el proceso tiene sesiones asociadas, se perderán también.`
                : `Vas a eliminar la sesion "${deleteConfirmTarget?.title}". Esta accion no se puede deshacer.`}
            </H5>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <GhostButton type="button" onClick={() => setDeleteConfirmTarget(null)}>
                Cancelar
              </GhostButton>
              <DangerButton type="button" onClick={handleConfirmDelete}>
                Eliminar
              </DangerButton>
            </div>
          </div>
        </Modal>
      </Page>
    </MainLayout>
  )
}

export default CertificationProcesses
